import { useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, BadgePercent } from "lucide-react";
import { ChapterHeading, Reveal } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });

const PLANS = [
  {
    name: "Start",
    monthly: 400,
    desc: "Para começar a responder em segundos.",
    features: ["1 agente de IA no WhatsApp", "Agendamento e confirmação automáticos", "Até 300 conversas por mês", "Setup guiado: taxa única de R$ 1.000", "Suporte por e-mail"],
    highlight: false,
  },
  {
    name: "Pro",
    monthly: 800,
    desc: "Para encher a agenda e recuperar clientes.",
    features: ["Tudo do Start", "Recuperação ativa de clientes inativos", "Conversas ilimitadas", "CliniAtiva Insights incluso", "Suporte prioritário no WhatsApp"],
    highlight: true,
  },
  {
    name: "Scale",
    monthly: null,
    desc: "Para operações com múltiplas unidades.",
    features: ["Tudo do Pro", "Multi-unidades e equipes", "Integrações personalizadas", "Gerente de conta dedicado", "SLA e acompanhamento estratégico"],
    highlight: false,
  },
];

const CLARITY = [
  { title: "Setup e implementação", text: "Taxa única de R$ 1.000, paga apenas uma vez no início. Inclui diagnóstico, configuração, testes e ativação. No Scale, o escopo é sob consulta." },
  { title: "Fidelidade", text: "Plano mensal sem fidelidade — cancele quando quiser. No anual, o compromisso é do período contratado." },
  { title: "Cancelamento", text: "O serviço permanece ativo até o fim do ciclo já pago. No plano anual não há reembolso proporcional." },
  { title: "40 primeiros clientes", text: "Acesso antecipado ao CliniAtiva Insights e implantação prioritária, sem custo adicional." },
];

export default function Pricing() {
  const [billing, setBilling] = useState("mensal");
  const anual = billing === "anual";

  return (
    <section id="planos" data-testid="pricing-section" className="relative py-28 sm:py-36">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <ChapterHeading
          index="09"
          eyebrow="Planos"
          align="center"
          lines={["Menos que um dia de", "agenda vazia por mês."]}
          sub="Setup de R$ 1.000 pago uma única vez, com implementação guiada em todos os planos. Plano mensal sem fidelidade."
        />

        <Reveal className="mt-12 flex flex-col items-center gap-3">
          <div data-testid="billing-toggle" className="flex items-center rounded-full border border-[var(--line)] bg-[var(--card)] p-1.5">
            {[
              { id: "mensal", label: "Mensal" },
              { id: "anual", label: "Anual" },
            ].map((b) => (
              <button
                key={b.id}
                data-testid={`billing-${b.id}`}
                onClick={() => setBilling(b.id)}
                className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-[background-color,color] duration-300 ${
                  billing === b.id ? "bg-petrol text-white dark:bg-turquoise dark:text-petrol" : "text-[var(--muted)]"
                }`}
              >
                {b.label}
              </button>
            ))}
          </div>
          {anual && (
            <motion.span
              data-testid="billing-discount-badge"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-1.5 rounded-full bg-turquoise/15 px-4 py-1.5 text-xs font-semibold text-[var(--brand)]"
            >
              <BadgePercent className="h-4 w-4" /> 20% de desconto no plano anual — cobrança antecipada (12 meses)
            </motion.span>
          )}
        </Reveal>

        <div className="mt-12 grid items-stretch gap-6 lg:grid-cols-3">
          {PLANS.map((p, i) => {
            const price = p.monthly === null ? null : anual ? p.monthly * 0.8 : p.monthly;
            return (
              <motion.div
                key={p.name}
                data-testid={`plan-card-${p.name.toLowerCase()}`}
                initial={{ opacity: 0, y: 44 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -6 }}
                className={`relative flex flex-col rounded-[28px] p-8 sm:p-10 ${
                  p.highlight
                    ? "grain bg-petrol text-white shadow-[0_40px_80px_-30px_rgba(18,60,74,0.55)] lg:-my-4 lg:py-14"
                    : "border border-[var(--line)] bg-[var(--card)]"
                }`}
              >
                {p.highlight && (
                  <span className="absolute -top-4 left-1/2 z-10 -translate-x-1/2 rounded-full bg-turquoise px-5 py-1.5 font-mono2 text-[10px] font-semibold uppercase tracking-[0.25em] text-petrol">
                    Mais popular
                  </span>
                )}
                <p className={`font-mono2 text-[11px] uppercase tracking-[0.3em] ${p.highlight ? "text-turquoise" : "text-[var(--brand)]"}`}>
                  {p.name}
                </p>
                <div className="mt-5 min-h-[76px]">
                  {price === null ? (
                    <span className={`font-display text-4xl font-extrabold tracking-tight sm:text-5xl ${p.highlight ? "text-white" : "text-[var(--ink)]"}`}>
                      Sob consulta
                    </span>
                  ) : (
                    <>
                      {anual && (
                        <span data-testid={`plan-original-${p.name.toLowerCase()}`} className={`block text-sm line-through ${p.highlight ? "text-sage/50" : "text-[var(--muted)]"}`}>
                          {brl.format(p.monthly)}/mês
                        </span>
                      )}
                      <span className={`font-display text-4xl font-extrabold tracking-tight sm:text-5xl ${p.highlight ? "text-white" : "text-[var(--ink)]"}`}>
                        {brl.format(price)}
                        <span className={`text-sm font-normal ${p.highlight ? "text-sage/70" : "text-[var(--muted)]"}`}>/mês</span>
                      </span>
                      {anual && (
                        <span className={`block text-xs ${p.highlight ? "text-sage/60" : "text-[var(--muted)]"}`}>
                          cobrança anual antecipada: {brl.format(price * 12)}/ano
                        </span>
                      )}
                    </>
                  )}
                </div>
                <p className={`mt-3 text-sm ${p.highlight ? "text-sage/80" : "text-[var(--muted)]"}`}>{p.desc}</p>
                <p
                  data-testid={`plan-setup-${p.name.toLowerCase()}`}
                  className={`mt-2 flex items-center gap-1.5 text-xs font-semibold ${p.highlight ? "text-turquoise" : "text-[var(--brand)]"}`}
                >
                  {p.monthly === null ? "+ setup sob consulta" : `+ setup de ${brl.format(1000)}, pago uma única vez`}
                </p>

                <ul className="mt-8 flex-1 space-y-3.5">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm">
                      <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${p.highlight ? "bg-turquoise/20 text-turquoise" : "bg-[var(--brand)]/12 text-[var(--brand)]"}`}>
                        <Check className="h-3 w-3" strokeWidth={3} />
                      </span>
                      <span className={p.highlight ? "text-sage" : "text-[var(--ink)]"}>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={`plan-cta-${p.name.toLowerCase()}`}
                  onClick={() => scrollToId("demonstracao")}
                  className={`group mt-10 flex items-center justify-center gap-2 rounded-full py-4 font-display text-sm font-bold transition-[transform,box-shadow,background-color] duration-300 hover:scale-[1.02] ${
                    p.highlight
                      ? "bg-turquoise text-petrol hover:shadow-[0_12px_36px_rgba(39,179,176,0.5)]"
                      : "border border-[var(--line)] text-[var(--ink)] hover:border-[var(--brand)] hover:bg-[var(--brand)]/5"
                  }`}
                >
                  Agendar demonstração
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </motion.div>
            );
          })}
        </div>

        <Reveal delay={0.15} className="mt-12">
          <div className="grid gap-4 rounded-3xl border border-[var(--line)] bg-[var(--card)] p-8 sm:grid-cols-2 lg:grid-cols-4">
            {CLARITY.map((c, i) => (
              <div key={c.title} data-testid={`plan-clarity-${i + 1}`}>
                <p className="font-display text-sm font-bold text-[var(--ink)]">{c.title}</p>
                <p className="mt-2 text-xs leading-relaxed text-[var(--muted)]">{c.text}</p>
              </div>
            ))}
          </div>
          <p className="mt-5 text-center text-xs text-[var(--muted)]">
            CliniAtiva Insights (incluso no Pro e no Scale): taxa de resposta, agendamentos por horário, picos de movimento e evolução da operação em tempo real.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
